<template>
  <div class="mt-2.5">
    <div class="mb-2.5 flex items-center text-sm font-medium text-gray-700">
      <div class="ml-6 flex flex-1">
        <div class="w-[42%]">{{ t("common.optionLabel") }}</div>
        <div v-if="!hidePrefillValue">{{ t("common.listEditor.prefillValueHeader") }}</div>
      </div>
    </div>
    <VueDraggableNext
      v-model="options"
      handle=".handle"
      class="space-y-2"
      @change="changeInOptions"
    >
      <div
        v-for="(option, index) in options"
        :key="`option-item-${index}`"
        class="flex items-center space-x-2 text-sm"
      >
        <Menu05Icon class="handle h-4 w-4 cursor-move text-gray-500" />
        <div class="flex flex-1 space-x-2">
          <UIInput
            :id="`option-label-${index}`"
            v-model="option.label"
            class="w-full"
            :placeholder="t('common.listEditor.labelPlaceholder')"
            :status="errors[index] ? 'error' : ''"
            @focus="onInputFocus"
            @input="emit('update:picklistOptions', [...options])"
          />
          <UIInput
            v-if="!hidePrefillValue"
            :id="`option-prefillValue-${index}`"
            v-model="option.prefillValue"
            class="w-full"
            :placeholder="t('common.listEditor.valuePlaceholder')"
            :status="errors[index] ? 'error' : ''"
            @focus="onInputFocus"
            @input="emit('update:picklistOptions', [...options])"
          />
        </div>
        <UIButton
          id="remove-option"
          :disabled="hidePrefillValue || !option.label"
          @click="() => prepareToRemoveOption(index)"
        >
          <Trash01Icon class="h-5 w-5" />
        </UIButton>
      </div>
    </VueDraggableNext>
    <UIButton id="add-option" text class="mt-4" :disabled="isLastOptionEmpty" @click="addOption">
      <PlusIcon class="mr-1.5 h-5 w-5" />
      {{ t("common.addOption") }}
    </UIButton>
  </div>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>

<script setup lang="ts">
import { updateOptionsWithFocusPreservation } from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIInput } from "@gohighlevel/ghl-ui";
import { isEqual } from "lodash";
import { computed, onMounted, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";

interface ListOption {
  label: string;
  value?: string;
  prefillValue?: string;
  [key: string]: any;
}

const props = defineProps({
  picklistOptions: {
    type: Array as () => ListOption[],
    default: () => [],
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
  hidePrefillValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:picklistOptions"]);

const { t } = useI18n();

// Initialize with default or provided options
const options = ref<ListOption[]>(
  Array.isArray(props.picklistOptions) && props.picklistOptions.length > 0
    ? [...props.picklistOptions]
    : [{ label: "", value: "", prefillValue: "" }]
);

const activeInputId = ref<string | null>(null);
const activeInputPosition = ref<number | null>(null);
const indexToRemove = ref<number | null>(null);
const showDeleteModal = ref(false);
const errors = ref<string[]>([]);

onMounted(() => {
  emit("update:picklistOptions", [...options.value]);
});

watch(
  () => props.picklistOptions,
  (newOptions) => {
    if (!isEqual(newOptions, options.value)) {
      options.value = updateOptionsWithFocusPreservation(
        newOptions,
        options.value,
        { label: "", value: "", prefillValue: "" },
        activeInputId,
        activeInputPosition
      );
    }
  },
  { deep: true }
);

const onInputFocus = (e: FocusEvent) => {
  const target = e.target as HTMLInputElement;
  if (target?.id) {
    activeInputId.value = target.id;
  }
};

const validateOptions = () => {
  errors.value = options.value.map((option) =>
    option.label?.trim() === "" ? t("common.listEditor.optionRequiredError") : ""
  );
};

watch(
  () => options.value,
  () => {
    validateOptions();

    const activeElement = document.activeElement as HTMLElement;
    if (
      activeElement &&
      (activeElement.id?.startsWith("option-label-") ||
        activeElement.id?.startsWith("option-prefillValue-"))
    ) {
      emit("update:picklistOptions", [...options.value]);
    }
  },
  { deep: true }
);

const isLastOptionEmpty = computed(() => {
  const lastOption = options.value[options.value.length - 1];
  return (
    (lastOption.label?.trim() === "" || !lastOption.label) &&
    (lastOption.value?.trim() === "" || !lastOption.value)
  );
});

const addOption = () => {
  // Create a new array for reactivity
  const newOptions = [...options.value, { label: "", value: "", prefillValue: "" }];
  options.value = newOptions;
  emit("update:picklistOptions", [...newOptions]);
};

const prepareToRemoveOption = (index: number) => {
  if (options.value.length > 1) {
    if (props.customFieldCreated) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      // Create a copy of the options array for reactivity
      const newOptions = [...options.value];
      newOptions.splice(index, 1);
      options.value = newOptions;
      emit("update:picklistOptions", [...newOptions]);
    }
  }
};

const removeOption = async () => {
  if (indexToRemove.value !== null) {
    // Create a copy of the options array for reactivity
    const newOptions = [...options.value];
    newOptions.splice(indexToRemove.value, 1);
    options.value = newOptions;
    emit("update:picklistOptions", [...newOptions]);
  }
  showDeleteModal.value = false;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const changeInOptions = (e) => {
  if (e.moved) {
    emit("update:picklistOptions", [...options.value]);
  }
};
</script>
